import {lighten, saturate} from 'polished';
import {memo} from 'react';
import {createGlobalStyle} from 'styled-components';

import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

const nowIndicatorColor = (p: ThemeContextType) => saturate(0.1, lighten(0.06, p.theme.palette.red.shade40));
export const FullCalendarCustomStyles = memo(createGlobalStyle`
  .fullcalendar-wrapper {
    .fc-single-line-text {
      white-space: nowrap;
      overflow: hidden;
      font-weight: ${fontWeights.semibold};

      div {
        display: inline-block;
      }
    }

    .fc-small-line-text {
      white-space: nowrap;
      overflow: hidden;
      font-weight: ${fontWeights.semibold};
      font-size: ${fontSizes.tiny};

      div {
        display: inline-block;
      }
    }

    .fc-event {
      border-width: 0px;
      border-radius: 6px;
      box-sizing: border-box;
      padding: 4px 0px 4px 6px;
      margin-left: -1px;
      margin-right: 3px;
      margin-bottom: 2px;
      font-weight: ${fontWeights.semibold};
      font-size: ${fontSizes.verySmall};
      overflow: hidden;
      z-index: 0;

      .fc-event-content-wrapper {
        position: relative;
        z-index: 3;
        width: 100%;
        height: 100%;
        pointer-events: none;

        .fc-long-title {
          max-height: 4em;
          overflow: hidden;
        }

        .fc-date-text {
          font-weight: ${fontWeights.normal};
        }
      }
    }

    .fc-event.fc-event-focused {
      transition: .8s box-shadow;
    }

    .fc-event::after {
      width: 100%;
      height: 100%;
      background: ${(p) => p.theme.greys.white};
      position: absolute;
      left: 0px;
      top: 0px;
      opacity: 0;
      transition: .8s opacity;
      z-index: 2;
      content: " "
    }

    /* To support the highlight mode animation using ::after, we can't have any borders on events.
        Instead, we use ::before to create the appearance of a border using a white background. */
    .fc-event.fc-event-needs-action:not(.fc-standard-month-view-event)::before,
    .fc-event.fc-event-declined:not(.fc-standard-month-view-event)::before,
    .fc-event.fc-scheduling-slot:not(.fc-standard-month-view-event)::before
      {
      width: calc(100% - 2px);
      height: calc(100% - 2px);
      position: absolute;
      left: 1px;
      top: 1px;
      z-index: 1;
      border-radius: 5px;
      content: " "
    }

    .fc-event.fc-event-needs-action:not(.fc-standard-month-view-event)::before,
    .fc-event.fc-event-declined:not(.fc-standard-month-view-event)::before
      {
      background: ${(p) => p.theme.greys.white};
    }

    .fc-event.fc-scheduling-slot:not(.fc-standard-month-view-event)::before {
      background: ${(p) => p.theme.palette.green.shade10};
    }

    .fc-event.fc-event-tentative:not(.fc-standard-month-view-event) {
      border: none;
      padding: 4px 0px 4px 6px;
    }

    .fc-event.fc-scheduling-slot {
      /* Allow the scheduling slot to go 6px into the 8px gutter that blocks other events. */
      margin-right: -6px;

      .fc-event-content-wrapper {
        display: grid;
        grid-template-areas: "text icon";
        grid-template-columns: 1fr auto;
        column-gap: 4px;
        pointer-events: auto;

        .fc-date-text {
          grid-area: text;
        }

        .checkmark-icon {
          width: 14px;
          height: 14px;
          margin-right: 5px;
          margin-top: 1px;
          grid-area: icon;
          color: ${(p) => p.theme.palette.green.shade40};
        }

        .delete-icon {
          width: 14px;
          height: 14px;
          margin-right: 5px;
          margin-top: 1px;
          grid-area: icon;
          color: ${(p) => p.theme.greys.shade50};
          display: none;

          &:hover {
            color: ${(p) => p.theme.greys.shade60};
          }
        }
      }

      .fc-event-content-wrapper:hover {
        .checkmark-icon {
          display: none;
        }

        .delete-icon {
          display: block;
        }
      }
    }

    .fc-event.fc-scheduling-slot.fc-scheduling-slot-past {
      .fc-event-content-wrapper {
        .checkmark-icon {
          color: ${(p) => p.theme.palette.green.shade30};
        }
      }
    }

    .fc-event.fc-event-declined {
      text-decoration: line-through;

      div {
        text-decoration: line-through;
      }
    }

    .fc-event.fc-event-cancelled {
      text-decoration: line-through;

      div {
        text-decoration: line-through;
      }
    }    

    .fc-day-grid-event {
      margin-right: 15px;
    }

    .fc-hover-placeholder {
      width: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${(p) => p.theme.alphas.gray10};

      svg {
        height: 16px;
        width: 16px;
        color: ${(p) => p.theme.greys.shade50};
      }
    }
  }

  .fc .fc-view {
    /* Reset FullCalendar's default z index. */
    z-index: 0 !important;

    .fc-body > tr > .fc-widget-content {
      border: none;
    }

    .fc-axis {
      width: 50px !important;
    }

    .fc-axis.fc-time {
      color: ${(p) => p.theme.greys.shade70};
      font-size: ${fontSizes.veryTiny};
      border-color: ${(p) => p.theme.greys.white};
      transform: translateY(-11px);

      span {
        margin-right: 5px;
      }
    }

    .fc-resizer.fc-end-resizer {
      cursor: ns-resize;
    }

    .fc-head-container {
      border-top: none;
      border-left: none;
      border-right: none;

      .fc-axis {
        border: none;
      }

      .fc-day-header {
        border: none;
        text-align: left;
        padding-left: 12px;
        padding-bottom: 5px;
        font-weight: normal;

        .fc-day-header-content-weekday {
          font-size: ${fontSizes.medium};
          color: ${(p) => p.theme.greys.shade70};
          line-height: 22px;
        }

        .fc-day-header-content-day {
          font-size: 20px;
          font-weight: ${fontWeights.medium};
          color: ${(p) => p.theme.greys.shade80};
          line-height: 32px;
        }
      }

      .fc-day-header.fc-today {
        background-color: ${(p) => p.theme.greys.white} !important;
        font-weight: ${fontWeights.bold};

        .fc-day-header-content-weekday {
          color: ${(p) => p.theme.palette.blue.shade40};
        }

        .fc-day-header-content-day {
          color: ${(p) => p.theme.palette.blue.shade40};
        }
      }
    }

    .fc-highlight {
      background-color: ${(p) => p.theme.palette.blue.shade40};
      opacity: 0.1;
    }

    /* Event grid section styles. */
    .fc-time-grid {
      .fc-slats td {
        height: 25px;
      }

      .fc-slats .fc-minor td {
        border-top-color: ${(p) => p.theme.greys.white};
      }
    }

    .fc-body hr.fc-divider.fc-widget-header {
      padding: 0px;
      border-width: 1px 0px 0px 0px;
    }

    .fc-now-indicator {
      border-top: 2px solid ${nowIndicatorColor};
      z-index: 4;
    }

    .fc-now-indicator.fc-now-indicator-arrow {
      display: none;
    }

    .fc-now-indicator.fc-now-indicator-line::before {
      content: '';
      position: absolute;
      top: -6px;
      left: -5px;
      width: 10px;
      height: 10px;
      border-radius: 5px;
      background-color: ${nowIndicatorColor};
      pointer-events: none;
    }
  }

  /* Timegrid specific styles (day and week) */
  .fc-view-container .fc-view.fc-timeGrid-view {
    .fc-today {
      background: ${(p) => p.theme.greys.white} !important;
    }

    .fc-event-container {
      margin-left: 0px;
      margin-right: 15px;
    }

    /* All day event section styles. */
    .fc-day-grid {
      max-height: 90px;
      overflow-y: scroll;
      overflow-x: hidden;

      .fc-axis.fc-widget-content {
        border-right: none;
      }

      .fc-day.fc-widget-content {
        border-left: none;
      }

      .fc-row.fc-week.fc-widget-content {
        /* Override the inline style that fullcalendar applies. */
        margin-right: 0px !important;
        border-right: none;
      }

      /* Adding padding to the all day event section. */
      .fc-row .fc-content-skeleton {
        padding-top: 10px;
        padding-bottom: 12px;
        min-height: 28px;
      }

      /* Adding padding to multi-day all day events. */
      .fc-day-grid-event.fc-h-event.fc-not-start {
        padding-left: 5px;
      }
    }
  }

  /* Month-view specific styles */
  .fc-view-container .fc-view.fc-dayGridMonth-view {
    td {
      border-color: ${(p) => p.theme.greys.shade30};
    }

    .fc-head-container.fc-widget-header {
      border-color: ${(p) => p.theme.greys.shade40};
    }

    .fc-today {
      background: ${(p) => p.theme.greys.shade10} !important;
    }

    .fc-head-container .fc-day-header {
      padding-left: 11px;
      padding-bottom: 11px;
      font-weight: ${fontWeights.normal};
      font-size: ${fontSizes.medium};
      color: ${(p) => p.theme.greys.shade80};
    }

    .fc-day-top.fc-today .fc-day-number {
      color: ${(p) => p.theme.palette.blue.shade40};
      font-weight: ${fontWeights.bold};
    }

    .fc-day-top {
      border-color: ${(p) => p.theme.greys.shade30};

      .fc-day-number {
        float: left;
        padding-top: 10px;
        padding-left: 10px;
        padding-bottom: 8px;
        color: ${(p) => p.theme.greys.shade80};
        font-size: ${fontSizes.large};
      }
    }

    .fc-event {
      padding-left: 8px;
      margin-right: 10px;
      background-color: ${(p) => p.theme.alphas.transparent};
      height: 23px;

      .fc-event-content-wrapper {
        font-size: 11px;
        display: grid;
        grid-template-columns: auto auto 1fr;
        white-space: nowrap;
        overflow: hidden;
        align-items: center;

        .fc-month-event-dot {
          width: 8px;
          height: 8px;
          border-width: 1px;
          border-style: solid;
          border-radius: 4px;
          box-sizing: border-box;
          margin-right: 5px;
        }

        .fc-date-text-month {
          font-weight: ${fontWeights.medium};
          color: ${(p) => p.theme.greys.shade70};
          margin-right: 6px;
        }

        .fc-title-text-month {
          color: ${(p) => p.theme.greys.shade90};
        }
      }

      .fc-event-content-wrapper :last-child {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }

    .fc-event:hover {
      background-color: ${(p) => p.theme.alphas.gray10};
    }

    .fc-more-cell {
      div {
        margin-right: 8px;
        border-radius: 6px;
        margin-left: -1px;
        padding-left: 8px;
        height: 23px;
        line-height: 23px;

        .fc-more {
          display: block;
          margin: 0px;
          cursor: default;
          font-weight: ${fontWeights.bold};
          color: ${(p) => p.theme.greys.shade80};
          margin-left: 0px;
          text-decoration: none;
        }
      }

      div:hover {
        background: ${(p) => p.theme.alphas.gray10};
      }
    }
  }

  /* Week-view specific styles*/
  .fc-view-container .fc-view.fc-timeGridWeek-view {
    .fc-head-container.fc-widget-header {
      border-color: ${(p) => p.theme.greys.shade40};
    }

    .fc-today {
      background: ${(p) => p.theme.greys.shade10} !important;
    }

    .fc-slats .fc-minor td {
      border-top: none;
    }

    .fc-event-container {
      margin-left: 0px;
      margin-right: 8px;
    }

    .fc-axis.fc-time {
      border-right-color: ${(p) => p.theme.greys.shade30};
    }
  }

  /* In preview mode, we don't want to show any scrollbars on the calendar. */
  .fullcalendar-wrapper.fc-wrapper-preview-mode .fc .fc-view .fc-time-grid-container {
    overflow: hidden !important;
  }

  .fullcalendar-wrapper.fc-highlight-mode {
    .fc-event:not(.fc-event-focused)::after {
      opacity: .4;
    }

    .fc-event.fc-event-focused {
      ${(p) =>
        boxShadow(
          p.theme,
          `0px 6px 20px rgba(0, 0, 0, 0.16), 0px 1px 6px rgba(0, 0, 0, 0.08), 0px 6px 10px rgba(0, 0, 0, 0.02)`,
        )};
      z-index: 4 !important; /* Ensure that the focused event appears on top of others in the grid. */
    }
  }

  .fullcalendar-wrapper:not(.fc-wrapper-preview-mode):not(.fc-wrapper-month-view) {
    .fc-event-with-hover:hover {
      ${(p) => boxShadow(p.theme, `0px 2px 10px rgba(0, 0, 0, 0.2)`)};
    }
  }

  .fullcalendar-wrapper.fc-fullscreen {
    .fc-event.fc-event-focused {
      ${(p) =>
        boxShadow(
          p.theme,
          `0px 1px 4px rgba(0, 0, 0, 0.1), 0px 6px 10px rgba(0, 0, 0, 0.08), 0px 4px 20px rgba(0, 0, 0, 0.12)`,
        )};
    }

    @keyframes changeOpacity {
      0% {
        opacity: .3;
      }

      50% {
        opacity: 0;
      }

      100% {
        opacity: .3;
      }
    }

    .fc-event.fc-event-focused::after {
      background: ${(p) => p.theme.greys.black};
      animation: changeOpacity 2s infinite;
      animation-timing-function: linear;
    }

    .fc-view .fc-event.fc-event-popover {
      ${(p) =>
        boxShadow(
          p.theme,
          `0px 1px 4px rgba(0, 0, 0, 0.1), 0px 6px 10px rgba(0, 0, 0, 0.08), 0px 4px 20px rgba(0, 0, 0, 0.12)`,
        )};
    }
  }
`);
